import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import staioIcon from "@/assets/images/staio-icon.png";
import {
  Shield,
  Globe,
  Clock,
  CreditCard,
  Users,
  ArrowRight,
  CheckCircle2,
  Building2,
  Sparkles,
  Lock,
  BarChart3,
  DoorOpen,
  MessageSquare,
  Zap,
  TrendingUp,
} from "lucide-react";

const stats = [
  { value: "500+", label: "Properties Managed" },
  { value: "50+", label: "Property Managers" },
  { value: "10,000+", label: "Guest Nights" },
  { value: "99.5%", label: "Guest Satisfaction" },
];

const features = [
  {
    icon: Shield,
    title: "Professional Management",
    desc: "Every property is managed by vetted professionals with real-time operational tools.",
  },
  {
    icon: Lock,
    title: "Secure Payments",
    desc: "End-to-end payment tracking with bank-grade encryption for all financial data.",
  },
  {
    icon: Sparkles,
    title: "Smart Technology",
    desc: "Smart lock integration, automated cleaning workflows, and AI-powered guest support.",
  },
  {
    icon: Globe,
    title: "Global Marketplace",
    desc: "Browse apartments, villas, and homes across destinations with real-time availability.",
  },
  {
    icon: Zap,
    title: "Instant Booking",
    desc: "Book in seconds with immediate confirmation, access codes, and digital property guides.",
  },
  {
    icon: MessageSquare,
    title: "Multilingual Support",
    desc: "Full English and Spanish support for guests and property managers.",
  },
];

const capabilities = [
  "Multi-tenant architecture with data isolation",
  "Role-based access control (6 user roles)",
  "Channel management (Airbnb, Booking.com, VRBO)",
  "Automated cleaning and maintenance workflows",
  "Real-time financial tracking and reconciliation",
  "Tax compliance with field-level encryption",
  "Guest CRM with pipeline management and tagging",
  "Workflow automation engine with 8 trigger types",
  "Lumi AI business intelligence and insights",
  "Commission management and affiliate tracking",
  "Custom fields and 25+ feature flags per organization",
  "Push notifications and AI-powered guest chatbot",
];

const scaleMetrics = [
  {
    icon: Users,
    label: "6 User Roles",
    detail: "Admin, Property Manager, Cleaner, Maintenance, Affiliate, Guest",
  },
  {
    icon: BarChart3,
    label: "25+ Feature Flags",
    detail: "Per organization customization",
  },
  {
    icon: DoorOpen,
    label: "Full API Integration",
    detail: "Ready for third-party connections",
  },
  {
    icon: Clock,
    label: "iCal Sync",
    detail: "Cross-platform booking coordination",
  },
];

const outfitHeading = { fontFamily: "'Outfit', sans-serif" } as const;

export default function MarketplaceAbout() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3 h-16">
          <Link href="/marketplace">
            <div className="flex items-center gap-2 cursor-pointer" data-testid="link-logo">
              <img src={staioIcon} alt="Staio" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold" style={outfitHeading}>Staio</span>
            </div>
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <Link href="/marketplace">
              <Button variant="ghost" size="sm" data-testid="link-browse">Browse Properties</Button>
            </Link>
            <Link href="/for-hosts">
              <Button variant="outline" size="sm" data-testid="link-hosts">For Property Managers</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative py-24 px-4 bg-gradient-to-br from-primary/10 via-background to-primary/5" data-testid="section-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 tracking-tight"
            style={outfitHeading}
            data-testid="text-hero-title"
          >
            The Future of Short-Term Rental Management
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10" data-testid="text-hero-subtitle">
            Staio is an enterprise-grade property management platform that connects guests with professionally managed vacation rentals worldwide.
          </p>
          <Link href="/marketplace">
            <Button size="lg" data-testid="button-explore">
              Explore Properties <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="bg-primary/5 border-y" data-testid="section-stats">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((stat) => (
              <div key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-3xl sm:text-4xl font-bold text-primary" style={outfitHeading}>
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4" data-testid="section-why-staio">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4" style={outfitHeading} data-testid="text-why-title">
            Why Staio?
          </h2>
          <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12">
            Everything you need to book with confidence and enjoy a seamless stay.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((item) => (
              <Card key={item.title} data-testid={`card-feature-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2" style={outfitHeading}>{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-muted/30" data-testid="section-enterprise">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl font-bold mb-4" style={outfitHeading} data-testid="text-enterprise-title">
              Enterprise Platform
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Built from the ground up for professional property management at scale.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-3" data-testid="list-capabilities">
              {capabilities.map((cap) => (
                <div key={cap} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{cap}</span>
                </div>
              ))}
            </div>

            <Card className="bg-primary/5 border-primary/20" data-testid="card-built-for-scale">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold" style={outfitHeading}>Built for Scale</h3>
                </div>
                <div className="space-y-5">
                  {scaleMetrics.map((metric) => (
                    <div key={metric.label} className="flex items-start gap-3" data-testid={`metric-${metric.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <metric.icon className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{metric.label}</div>
                        <div className="text-xs text-muted-foreground">{metric.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-primary/5" data-testid="section-cta-hosts">
        <div className="max-w-3xl mx-auto text-center">
          <CreditCard className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4" style={outfitHeading} data-testid="text-cta-title">
            Ready to grow your rental business?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto" data-testid="text-cta-desc">
            Join Staio and access enterprise tools that streamline operations, automate workflows, and maximize your revenue across every channel.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link href="/for-hosts">
              <Button size="lg" data-testid="button-learn-more">
                Learn More <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link href="/for-hosts#contact">
              <Button size="lg" variant="outline" data-testid="button-get-in-touch">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={staioIcon} alt="Staio" className="w-6 h-6 rounded-md" />
              <span className="text-sm font-semibold" style={outfitHeading}>Staio</span>
            </div>
            <div className="flex items-center gap-4 flex-wrap text-sm text-muted-foreground">
              <Link href="/marketplace"><span className="cursor-pointer hover:text-foreground">Browse</span></Link>
              <Link href="/for-hosts"><span className="cursor-pointer hover:text-foreground">For Hosts</span></Link>
              <Link href="/about"><span className="cursor-pointer hover:text-foreground">About</span></Link>
            </div>
            <p className="text-xs text-muted-foreground">Powered by Staio</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
